import { CheckCircle2, AlertCircle } from 'lucide-react'
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { cx } from './ui'

interface Toast {
  id: number
  message: ReactNode
  tone: 'success' | 'error'
}

interface ToastApi {
  success: (message: ReactNode) => void
  error: (message: ReactNode) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const push = useCallback((message: ReactNode, tone: Toast['tone']) => {
    const id = Date.now() + Math.random()
    setToasts((current) => [...current.slice(-2), { id, message, tone }])
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), tone === 'error' ? 6000 : 3500)
  }, [])
  const api = useMemo<ToastApi>(
    () => ({ success: (m) => push(m, 'success'), error: (m) => push(m, 'error') }),
    [push],
  )
  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4"
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={cx(
                'pointer-events-auto flex max-w-md animate-rise items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium shadow-pop',
                toast.tone === 'success' ? 'bg-ink-950 text-white' : 'bg-red-700 text-white',
              )}
            >
              {toast.tone === 'success' ? (
                <CheckCircle2 className="size-4.5 shrink-0 text-emerald-400" />
              ) : (
                <AlertCircle className="size-4.5 shrink-0" />
              )}
              {toast.message}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error('useToast must be used inside ToastProvider')
  return api
}
